// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_EVENT_TIMEZONE,
  EVENT_TIMEZONE_OTHER,
} from '../../domain/eventTimezones';
import {
  buildEventDefinitionPayload,
  getEventFormSubmitError,
} from './eventFormSubmit';

function todayLocalIsoDate() {
  const now = new Date();
  const pad = (segment) => String(segment).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function defaultImageForList(allowedImages) {
  if (allowedImages?.length) {
    return allowedImages[0];
  }
  return '';
}

export function emptyEventForm(allowedImages) {
  return {
    id: '',
    host: '',
    name: '',
    eventLocalDate: todayLocalIsoDate(),
    eventTimezone: DEFAULT_EVENT_TIMEZONE,
    eventStartTime: '',
    eventEndTime: '',
    location: '',
    cityState: '',
    link: '',
    linkText: '',
    description: '',
    latitude: '',
    longitude: '',
    cardImage: defaultImageForList(allowedImages),
    headerImage: defaultImageForList(allowedImages),
  };
}

function pickImageFromEvent(primary, legacy, allowedImages) {
  const primaryTrimmed = String(primary ?? '').trim();
  if (primaryTrimmed && allowedImages.includes(primaryTrimmed)) {
    return primaryTrimmed;
  }
  const legacyTrimmed = String(legacy ?? '').trim();
  if (legacyTrimmed && allowedImages.includes(legacyTrimmed)) {
    return legacyTrimmed;
  }
  return defaultImageForList(allowedImages);
}

export function eventRecordToForm(event, allowedImages) {
  const legacyImage = String(event.image ?? '').trim();
  const cardImage = pickImageFromEvent(
    event.image,
    event.cardImage,
    allowedImages,
  );
  const headerImage = pickImageFromEvent(
    event.headerImage,
    legacyImage,
    allowedImages,
  );
  const cityState = String(event.cityState ?? '').trim();
  let location = String(event.location ?? '').trim();
  if (!location) {
    location = String(event.venueName ?? '').trim();
  }
  return {
    id: event.id ?? '',
    host: event.host ?? '',
    name: event.name ?? '',
    eventLocalDate:
      String(event.eventLocalDate ?? '').trim() || todayLocalIsoDate(),
    eventTimezone:
      String(event.eventTimezone ?? '').trim() || DEFAULT_EVENT_TIMEZONE,
    eventStartTime: String(event.eventStartTime ?? '').trim(),
    eventEndTime: String(event.eventEndTime ?? '').trim(),
    location,
    cityState,
    link: event.link ?? '',
    linkText: event.linkText ?? '',
    description: event.description ?? '',
    latitude: event.latitude ?? '',
    longitude: event.longitude ?? '',
    cardImage,
    headerImage,
  };
}

/**
 * Shared add/edit event dialog state for Events list and Event detail page.
 * @param {{
 *   allowedImageChoices: string[],
 *   imagesLoading: boolean,
 *   createEvent: (data: object) => Promise<string>,
 *   updateEvent: (id: string, data: object) => Promise<void>,
 *   onSaved?: (info: { mode: string, id: string }) => void | Promise<void>,
 * }} options
 */
function useEventFormDialog({
  allowedImageChoices,
  imagesLoading,
  createEvent,
  updateEvent,
  onSaved,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm] = useState(() => emptyEventForm([]));
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!dialogOpen) return;
    if (!allowedImageChoices.length) return;
    setForm((prev) => {
      const defaultChoice = defaultImageForList(allowedImageChoices);
      const cardTrimmed = (prev.cardImage ?? '').trim();
      const headerTrimmed = (prev.headerImage ?? '').trim();
      const cardNext = allowedImageChoices.includes(cardTrimmed)
        ? prev.cardImage
        : defaultChoice;
      const headerNext = allowedImageChoices.includes(headerTrimmed)
        ? prev.headerImage
        : defaultChoice;
      if (cardNext === prev.cardImage && headerNext === prev.headerImage) {
        return prev;
      }
      return { ...prev, cardImage: cardNext, headerImage: headerNext };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- choices refresh while dialog open
  }, [dialogOpen, allowedImageChoices]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setErrorMsg('');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleTimezoneSelectChange = useCallback((event) => {
    const selectedPreset = event.target.value;
    setForm((prev) => {
      if (selectedPreset === EVENT_TIMEZONE_OTHER) {
        return { ...prev, eventTimezone: '' };
      }
      return { ...prev, eventTimezone: selectedPreset };
    });
  }, []);

  const openAddDialog = useCallback(() => {
    setErrorMsg('');
    setDialogMode('add');
    setForm(emptyEventForm(allowedImageChoices));
    setDialogOpen(true);
  }, [allowedImageChoices]);

  const openEditDialog = useCallback(
    (event) => {
      setErrorMsg('');
      setDialogMode('edit');
      setForm(eventRecordToForm(event, allowedImageChoices));
      setDialogOpen(true);
    },
    [allowedImageChoices],
  );

  const handleSubmitEvent = useCallback(async () => {
    const validationError = getEventFormSubmitError(form, allowedImageChoices);
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    const eventLocalDateTrimmed = String(form.eventLocalDate ?? '').trim();
    const startTrimmed = String(form.eventStartTime ?? '').trim();
    const endTrimmed = String(form.eventEndTime ?? '').trim();
    const linkTrimmed = String(form.link ?? '').trim();
    const payload = buildEventDefinitionPayload(
      form,
      eventLocalDateTrimmed,
      startTrimmed,
      endTrimmed,
      linkTrimmed,
    );

    setSubmitting(true);
    setErrorMsg('');
    try {
      let savedId = form.id;
      if (dialogMode === 'edit') {
        await updateEvent(form.id, payload);
        savedId = form.id;
      } else {
        savedId = await createEvent(payload);
      }
      if (onSaved) {
        await onSaved({ mode: dialogMode, id: savedId });
      }
      setDialogOpen(false);
      setForm(emptyEventForm(allowedImageChoices));
    } catch (err) {
      const message =
        err?.message && typeof err.message === 'string'
          ? err.message
          : dialogMode === 'edit'
            ? 'Failed to save event. Please try again.'
            : 'Failed to create event. Please try again.';
      setErrorMsg(message);
    } finally {
      setSubmitting(false);
    }
  }, [
    allowedImageChoices,
    createEvent,
    dialogMode,
    form,
    updateEvent,
    onSaved,
  ]);

  const editDialogProps = useMemo(
    () => ({
      open: dialogOpen,
      mode: dialogMode,
      submitting,
      errorMsg,
      form,
      availableImages: allowedImageChoices,
      imagesLoading,
      onClose: handleCloseDialog,
      onChange: handleFormChange,
      onTimezoneSelectChange: handleTimezoneSelectChange,
      onSubmit: handleSubmitEvent,
    }),
    [
      dialogOpen,
      dialogMode,
      submitting,
      errorMsg,
      form,
      allowedImageChoices,
      imagesLoading,
      handleCloseDialog,
      handleFormChange,
      handleTimezoneSelectChange,
      handleSubmitEvent,
    ],
  );

  return {
    editDialogProps,
    openAddDialog,
    openEditDialog,
    handleCloseDialog,
  };
}

export default useEventFormDialog;
